import * as moment from 'moment';

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { LocalStorage } from 'angular-web-storage';

import { ListResponseCountOptional } from '../../../../models/list-response';
import {
  Customer,
  CustomerFilters,
  CustomerSearchResult,
  CustomerTag,
  loadCustomerFiltersFromGetParams,
} from '../../../../models/customer';

import { AccountService } from '../../../../services/account/account.service';
import { CustomerService } from '../../../../services/customer/customer.service';
import {
  CallbackResult,
  CallbackResultStatus,
  getISO8601DateStringFromMoment,
  propExists,
  propExistsAndIsNonEmpty,
} from '../../../../utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomerFiltersModalComponent } from './modals/customer-filters-modal/customer-filters-modal.component';
import { OrganizationConfigService } from '../../../../services/organization-config/organization-config.service';
import { CustomCustomerField } from '../../../../models/organization-config';
import {
  CreateNewCustomerModalComponent,
} from './modals/create-new-customer-modal/create-new-customer-modal.component';
import * as ExportColumns from '../../../../models/exports/customers';
import * as ReportColumns from '../../../../models/report-columns/customers';
import { columnsOfType } from '../../../../reporting-core/column-types';
import { ReportCellType } from '../../../../reporting-core/cell-format';
import {
  SimpleMessageModalComponent,
} from '../../../../components/modals/simple-message-modal/simple-message-modal.component';
import {
  BulkAddOrRemoveCustomerTagModalComponent,
} from './modals/bulk-add-or-remove-customer-tag-modal/bulk-add-or-remove-customer-tag-modal.component';
import { EventType } from '../../../../models/event-type';
import { EventTypeService } from '../../../../services/event-type/event-type.service';
import { CustomerExportModalComponent } from './modals/customer-export-modal/customer-export-modal.component';
import {
  BulkAddOrRemoveCustomerChallengeModalComponent,
} from './modals/bulk-add-or-remove-customer-challenge-modal/bulk-add-or-remove-customer-challenge-modal.component';
import {
  BulkAddCustomersToAssociationModalComponent,
} from './modals/bulk-add-customers-to-association-modal/bulk-add-customers-to-association-modal.component';
import { Proficiency } from '../../../../models/proficiency';
import { ProficiencyService } from '../../../../services/proficiency/proficiency.service';
import { Association } from '../../../../models/association';
import { AssociationService } from '../../../../services/association/association.service';
import { Challenge } from '../../../../models/challenge';
import { ChallengeService } from '../../../../services/challenge/challenge.service';
import {
  getActiveCustomerFilterTags,
  usesSpecificAllthenticateProducts
} from '../../../../utils-models';
import { EventTypeStatus } from '../../../../models/event-type-enums';
import {
  BulkCreateGuestPassesModalComponent,
} from './modals/bulk-create-guest-passes-modal/bulk-create-guest-passes-modal.component';
import {
  BulkOperationCustomers,
  ObtainBulkOperationCustomersModalComponent,
} from './modals/obtain-bulk-operation-customers-modal/obtain-bulk-operation-customers-modal.component';
import { LocationService } from '../../../../services/location/location.service';
import {
  BulkAddCustomerAlertModalComponent,
} from './modals/bulk-add-customer-alert-modal/bulk-add-customer-alert-modal.component';
import {
  BulkSendAllthenticateInviteModalComponent,
} from './modals/bulk-send-allthenticate-invite-modal/bulk-send-allthenticate-invite-modal.component';
import { SavedReportViewReportType } from '../../../../models/saved-report-view';
import { ReportBaseDirective } from '../../report-base.directive';
import { SavedReportViewService } from '../../../../services/saved-report-view/saved-report-view.service';


const EXPORT_PAGE_SIZE = 1000;
const OBTAIN_BULK_CUSTOMERS_PAGE_SIZE = 1000;


/**
 * The component representing the list of customers.
 */
@Component({
  selector: 'app-customers',
  templateUrl: './customers.component.html',
  styleUrls: ['./customers.component.css']
})
export class CustomersComponent extends ReportBaseDirective<CustomerFilters> implements OnInit {

  public readonly MAX_BULK_ACTION_SIZE = 25000;

  public displayCheckInRangeCount = false;

  // Required by ReportBaseDirective.
  public filters: CustomerFilters = {};

  protected get DefaultColumns(): string[] {
    return this.displayCheckInRangeCount
      ? ReportColumns.DEFAULT_COLUMNS_CHECK_IN_RANGE
      : ReportColumns.DEFAULT_COLUMNS_PRIMARY;
  }

  protected get ReportColumnNames(): string[] {
    const base = this.displayCheckInRangeCount
      ? ReportColumns.REPORT_COLUMN_NAMES_CHECK_IN_RANGE
      : ReportColumns.REPORT_COLUMN_NAMES_PRIMARY;
    return [...base, ...this.customCustomerFields.map(f => f.name)];
  }

  protected readonly ColumnHelpText = ExportColumns.COLUMN_HELP_TEXT;
  protected readonly ReportColumnHeaderOverrides = ReportColumns.COLUMN_HEADER_OVERRIDES;

  protected get SavedViewExcludedProps(): string[] {
    // The recent-activity floor is applied automatically when a search is
    // performed; it should never be persisted into a saved view.
    const base = ['last_activity_timestamp_min'];
    if (this.displayCheckInRangeCount) {
      // Check-in date ranges are page-level filters in CCID mode, not
      // view-level filters.
      return [...base, 'check_in_range_start', 'check_in_range_end'];
    }
    return base;
  }

  protected get ReportType(): SavedReportViewReportType {
    return this.displayCheckInRangeCount
      ? SavedReportViewReportType.customerCheckInCountsInDateRange
      : SavedReportViewReportType.customers;
  }

  protected loadFiltersFromGetParams = loadCustomerFiltersFromGetParams;

  public readonly ColumnsWithCustomHTML = columnsOfType(ReportColumns.COLUMN_TYPES, ReportCellType.custom);
  public readonly ObjectIDColumns = columnsOfType(ReportColumns.COLUMN_TYPES, ReportCellType.objectId);
  public readonly CurrencyColumns: string[] = [];
  public readonly DateColumns = columnsOfType(ReportColumns.COLUMN_TYPES, ReportCellType.date);

  // Giving this a project-unique name ensures it is not shared with other saved
  // pageSize variables.
  @LocalStorage() public pageSizeCustomers;

  public customerList: ListResponseCountOptional<CustomerSearchResult>;
  public tags: CustomerTag[] = null;
  public eventTypes: EventType[] = null;
  public proficiencies: Proficiency[] = null;
  public associations: Association[] = null;
  public challenges: Challenge[] = null;
  public customCustomerFields: CustomCustomerField[] = [];
  public customCustomerFieldsLoading = true;

  public selectedCustomerIds: number[] = [];
  public selectedCustomerNames: {[key: number]: string} = {};
  public allCustomersMatchingFiltersSelected = false;

  public loadingCustomers = false;
  public loadingTags = false;
  public loadingEventTypes = false;
  public loadingProficiencies = false;
  public loadingAssociations = false;
  public loadingChallenges = false;

  // Make browser history available to the template.
  public history: History;

  // The most recent result returned from a modal.
  public modalResult: CallbackResult = null;

  // The most recently created customer returned from the "Create Customer" modal.
  public createdCustomer: Customer = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public CallbackResultStatus = CallbackResultStatus;
  public propExists = propExists;
  public readonly ExportColumns = ExportColumns;
  public readonly ReportColumns = ReportColumns;

  // The number of customer list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private customerListRequestCount = 0;

  constructor(
    public accountService: AccountService,
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    protected savedReportViewService: SavedReportViewService,
    protected modalService: NgbModal,
    private customerService: CustomerService,
    private orgConfigService: OrganizationConfigService,
    private eventTypeService: EventTypeService,
    private proficiencyService: ProficiencyService,
    private associationService: AssociationService,
    private challengeService: ChallengeService,
    private locationService: LocationService,
  ) {
    super(
      activatedRoute,
      router,
      savedReportViewService,
      accountService,
      modalService,
    );
  }

  ngOnInit() {
    // Resolve the display mode synchronously before super.ngOnInit() runs, so
    // ReportType / DefaultColumns / ReportColumnNames resolve to the correct
    // mode-specific values when the directive loads the default view.
    this.displayCheckInRangeCount =
      !!this.activatedRoute.snapshot.data.displayCheckInRangeCount;

    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    if (!this.pageSizeCustomers) {
      this.pageSizeCustomers = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeCustomers = this.pageSizeCustomers;

    // Make browser history available to the template.
    this.history = history;

    super.ngOnInit();

    this.loadTags();
    this.loadEventTypes();
    this.loadProficiencies();
    this.loadAssociations();
    this.loadChallenges();
    this.loadCustomCustomerFields();
  }

  /**
   * Load the page of customers corresponding to the current page and filter
   * selections.
   */
  public loadReport() {
    this.loadingCustomers = true;

    // Increment the request count and save a local copy.
    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    let getCustomersPromise;
    if (this.displayCheckInRangeCount) {
      getCustomersPromise = this.customerService.getCustomersWithCheckInCountInRange(
        this.pageNumber,
        this.pageSizeCustomers,
        this.filters,
        false
      );
    } else if (this.hasSearchFilterApplied()) {
      // The /customer-search/ endpoint has barcode and telephone exact-match
      // fast-paths that the general /customers/ filter lacks, so it must be
      // used whenever a search term is active. The trade-off is that columns
      // beyond first/last/email/preferred-name/birthday will be empty for
      // search results.
      getCustomersPromise = this.customerService.searchCustomers(
        this.pageNumber,
        this.pageSizeCustomers,
        this.filters,
        true,
        false
      );
    } else {
      // No active search: use the full /customers/ endpoint so configurable
      // columns (custom fields, addresses, membership flags, etc.) have data
      // to render.
      getCustomersPromise = this.customerService.getCustomers(
        this.pageNumber,
        this.pageSizeCustomers,
        this.filters,
        true
      );
    }

    getCustomersPromise
      .then(response => {
        // Only update the customer list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response;
          this.selectedCustomerIds = [];
          this.selectedCustomerNames = {};
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingCustomers = false;
        }
      });
  }

  protected setInitialFilterParams(replaceFilters: boolean) {
    if (replaceFilters) {
      this.filters = {};
    }
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.customerList) { return 1; }

    // If this is less than a full page of results, it is the last page.
    if (this.customerList.results.length < this.pageSizeCustomers) { return this.pageNumber; }

    // If we don't have count information, always allow the user to navigate two pages further.
    if (!propExists(this.customerList.count)) { return this.pageNumber + 2; }

    return Math.ceil(this.customerList.count / this.pageSizeCustomers);
  }

  /**
   * Handle a change in the customer filters. The list of customers will be
   * filtered according to the new values.
   */
  public handleCustomerFilterChange(isSearch: boolean = false) {
    // Show the loading message right away, so there isn't a lag during debounce.
    this.loadingCustomers = true;

    const orgConfig = this.orgConfigService.getOrganizationConfig();
    const mustApplyRecentActivityFilter = (
      isSearch &&
      propExistsAndIsNonEmpty(this.filters.search) &&
      orgConfig &&
      orgConfig.customer_search_recent_activity_window_months
    );
    if (mustApplyRecentActivityFilter) {
      this.filters.last_activity_timestamp_min = getISO8601DateStringFromMoment(
        moment().subtract(orgConfig.customer_search_recent_activity_window_months, 'months')
      );
    } else if (!propExistsAndIsNonEmpty(this.filters.search)) {
      this.filters.last_activity_timestamp_min = undefined;
    }

    this.handleFilterChange();
  }

  /**
   * Whether a non-zero number of customer results have been loaded.
   */
  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      this.customerList.results &&
      (this.customerList.results.length > 0)
    );
  }

  public showBulkActions(): boolean {
    return (
      this.customerResultsPresent() &&
      (this.selectedCustomerIds.length > 0)
    );
  }

  public tooManyCustomersForBulkAction(): boolean {
    if (!this.customerResultsPresent()) { return false; }

    if (this.allCustomersMatchingFiltersSelected) {
      return this.customerList.count > this.MAX_BULK_ACTION_SIZE;
    } else {
      return this.selectedCustomerIds.length > this.MAX_BULK_ACTION_SIZE;
    }
  }

  public allCustomersSelected(): boolean {
    return (
      this.customerResultsPresent() &&
      (this.selectedCustomerIds.length === this.customerList.results.length)
    );
  }

  public hasMultiplePages(): boolean {
    return (
      this.customerResultsPresent() &&
      (this.customerList.count > this.customerList.results.length)
    );
  }

  /**
   * Toggle the selection of the given customer.
   */
  public toggleCustomerSelection(customer: CustomerSearchResult) {
    const selectedCustomerIndex = this.selectedCustomerIds.indexOf(customer.id);

    if (selectedCustomerIndex >= 0) {
      this.selectedCustomerIds.splice(selectedCustomerIndex, 1);
      delete this.selectedCustomerNames[customer.id];
    } else {
      this.selectedCustomerIds.push(customer.id);
      this.selectedCustomerNames[customer.id] = customer.first_name + ' ' + customer.last_name;
    }

    this.allCustomersMatchingFiltersSelected = false;
  }

  /**
   * Toggle the selection of all customers. If all customers are already selected,
   * this will deselect all customers.
   */
  public toggleCustomerSelectAll() {
    const allCustomersAreSelected = (
      this.selectedCustomerIds.length !== this.customerList.results.length
    );

    this.selectedCustomerIds = [];
    this.selectedCustomerNames = {};

    if (allCustomersAreSelected) {
      for (const customer of this.customerList.results) {
        this.selectedCustomerIds.push(customer.id);
        this.selectedCustomerNames[customer.id] = customer.first_name + ' ' + customer.last_name;
      }
    }

    this.allCustomersMatchingFiltersSelected = false;
  }

  public customerAgeAndBirthday(customer: CustomerSearchResult) {
    const birthday = moment(customer.birthday, 'YYYY-MM-DD');
    const birthdayStr = birthday.format('ll');

    const age = moment().diff(birthday, 'years');
    const ageStr = age.toString();

    return ageStr + ' / ' + birthdayStr;
  }

  public getActiveFilters(): string[] {
    return getActiveCustomerFilterTags(
      this.filters,
      this.orgConfigService,
      this.eventTypes,
      this.proficiencies,
      this.associations,
      this.challenges,
      this.locationService.getLocations(),
    );
  }

  /**
   * Open a modal for editing the customer filters.
   */
  public openEditFiltersModal() {
    if (!this.editFiltersModalIsAvailable()) { return; }

    this.modalResult = null;
    this.createdCustomer = null;

    const modalRef = this.modalService.open(CustomerFiltersModalComponent);
    modalRef.componentInstance.inputCustomerFilters = this.filters;
    modalRef.componentInstance.tags = this.tags;
    modalRef.componentInstance.eventTypes = this.eventTypes;
    modalRef.componentInstance.proficiencies = this.proficiencies;
    modalRef.componentInstance.associations = this.associations;
    modalRef.componentInstance.challenges = this.challenges;
    modalRef.componentInstance.locations = this.locationService.getLocations();
    modalRef.componentInstance.savedCallback = (newFilters: CustomerFilters) => {
      this.filters = newFilters;
      this.handleCustomerFilterChange();
    };
  }

  /**
   * Open a modal for exporting the customers list, with the currently-selected
   * filters.
   */
  public openExportListModal() {
    if (!this.accountService.getMyAccount().can_export_customers) {
      const messageModalRef = this.modalService.open(SimpleMessageModalComponent);
      messageModalRef.componentInstance.headerText = 'Export Customers';
      messageModalRef.componentInstance.messageText = 'You do not have permission to export customers.';
      messageModalRef.componentInstance.messageAlertClass = 'alert-danger';
      return;
    }

    this.modalResult = null;
    this.createdCustomer = null;

    const modalRef = this.modalService.open(CustomerExportModalComponent);
    modalRef.componentInstance.getPage = (pageNum: number) => {
      if (this.displayCheckInRangeCount) {
        return this.customerService.getCustomersWithCheckInCountInRange(
          pageNum,
          EXPORT_PAGE_SIZE,
          this.filters,
          false
        );
      } else {
        return this.customerService.getCustomers(
          pageNum,
          EXPORT_PAGE_SIZE,
          this.filters,
          false
        );
      }
    };
    modalRef.componentInstance.getPageParentsAndGuardiansOnly = (pageNum: number) => {
      return this.customerService.getCustomerParentsAndGuardiansOnly(pageNum, EXPORT_PAGE_SIZE, this.filters);
    };
    modalRef.componentInstance.getContentItem = ExportColumns.getContentItem;
    modalRef.componentInstance.totalNumPages = Math.ceil(this.customerList.count / EXPORT_PAGE_SIZE);
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
    };
    // Pre-select the columns currently displayed in the table.
    modalRef.componentInstance.columnNames = this.ReportColumnNames;
    modalRef.componentInstance.preSelectedColumnNames = [...this.columns];
    modalRef.componentInstance.customFieldColumnNames =
      this.customCustomerFields.map(f => f.name);
    if (this.displayCheckInRangeCount) {
      modalRef.componentInstance.exportNameHumanReadable = 'Customer Check-In Counts in Range';
      modalRef.componentInstance.exportFilenamePrefix = 'Customer_Check_In_Counts_In_Range';
    } else {
      modalRef.componentInstance.exportNameHumanReadable = 'Customers';
      modalRef.componentInstance.exportFilenamePrefix = 'Customers';
    }
  }

  public openCreateCustomerModal() {
    this.modalResult = null;
    this.createdCustomer = null;

    const modalRef = this.modalService.open(CreateNewCustomerModalComponent);
    modalRef.componentInstance.savedCallback = (result: Customer) => {
      this.createdCustomer = result;
      this.loadReport();
    };
  }

  /**
   * Navigate to the bulk add credit or voucher component, with the selected
   * customer IDs and emails in the history state.
   */
  public goToAddCreditOrVoucher() {
    if (this.allCustomersMatchingFiltersSelected) {
      this.obtainCustomersForBulkAction(
        true,
        (result: BulkOperationCustomers) => {
          this.goToAddCreditOrVoucherForCustomers(
            result.customerIds,
            result.customerNames
          );
        }
      );
    } else {
      this.goToAddCreditOrVoucherForCustomers(
        this.selectedCustomerIds,
        this.selectedCustomerNames
      );
    }
  }

  /**
   * Navigate to the bulk create booking component, with the selected
   * customer IDs and emails in the history state.
   */
  public goToCreateBooking() {
    if (this.allCustomersMatchingFiltersSelected) {
      this.obtainCustomersForBulkAction(
        true,
        (result: BulkOperationCustomers) => {
          this.goToCreateBookingForCustomers(
            result.customerIds,
            result.customerNames
          );
        }
      );
    } else {
      this.goToCreateBookingForCustomers(
        this.selectedCustomerIds,
        this.selectedCustomerNames
      );
    }
  }

  public openBulkAddOrRemoveTagModal(isAdd: boolean) {
    if (this.loadingTags) { return; }

    if (this.allCustomersMatchingFiltersSelected) {
      this.obtainCustomersForBulkAction(
        false,
        (result: BulkOperationCustomers) => {
          this.openBulkAddOrRemoveTagModalForCustomers(
            isAdd,
            result.customerIds
          );
        }
      );
    } else {
      this.openBulkAddOrRemoveTagModalForCustomers(isAdd, this.selectedCustomerIds);
    }
  }

  public openBulkAddOrRemoveChallengeModal(isAdd: boolean) {
    if (this.loadingTags) { return; }

    if (this.allCustomersMatchingFiltersSelected) {
      this.obtainCustomersForBulkAction(
        false,
        (result: BulkOperationCustomers) => {
          this.openBulkAddOrRemoveChallengeModalForCustomers(
            isAdd,
            result.customerIds
          );
        }
      );
    } else {
      this.openBulkAddOrRemoveChallengeModalForCustomers(isAdd, this.selectedCustomerIds);
    }
  }

  public openBulkAddToAssociationModal() {
    if (this.loadingAssociations) { return; }

    if (this.allCustomersMatchingFiltersSelected) {
      this.obtainCustomersForBulkAction(
        false,
        (result: BulkOperationCustomers) => {
          this.openBulkAddToAssociationModalForCustomers(result.customerIds);
        }
      );
    } else {
      this.openBulkAddToAssociationModalForCustomers(this.selectedCustomerIds);
    }
  }

  public openBulkAddGuestPassesModal() {
    if (this.allCustomersMatchingFiltersSelected) {
      this.obtainCustomersForBulkAction(
        false,
        (result: BulkOperationCustomers) => {
          this.openBulkAddGuestPassesModalForCustomers(result.customerIds);
        }
      );
    } else {
      this.openBulkAddGuestPassesModalForCustomers(this.selectedCustomerIds);
    }
  }

  public openBulkAddAlertModal() {
    if (this.allCustomersMatchingFiltersSelected) {
      this.obtainCustomersForBulkAction(
        false,
        (result: BulkOperationCustomers) => {
          this.openBulkAddAlertModalForCustomers(result.customerIds);
        }
      );
    } else {
      this.openBulkAddAlertModalForCustomers(this.selectedCustomerIds);
    }
  }

  public usesSpecificAllthenticateProducts(): boolean {
    return usesSpecificAllthenticateProducts(
      this.orgConfigService.getOrganizationConfig()
    );
  }

  public openBulkSendAllthenticateInviteModal() {
    if (this.allCustomersMatchingFiltersSelected) {
      this.obtainCustomersForBulkAction(
        false,
        (result: BulkOperationCustomers) => {
          this.openBulkSendAllthenticateInviteModalForCustomers(result.customerIds);
        }
      );
    } else {
      this.openBulkSendAllthenticateInviteModalForCustomers(this.selectedCustomerIds);
    }
  }

  public editFiltersModalIsAvailable() {
    return !(
      this.loadingTags ||
      this.loadingEventTypes ||
      this.loadingProficiencies ||
      this.loadingAssociations ||
      this.loadingChallenges
    );
  }

  /**
   * Whether the given column's data is currently unavailable because the
   * Customers list is using the search endpoint (which returns a minimal
   * payload). Used by the template to mute the column header and surface a
   * tooltip explaining the absence.
   */
  public isColumnUnavailableDuringSearch(col: string): boolean {
    return (
      !this.displayCheckInRangeCount &&
      this.hasSearchFilterApplied() &&
      !ReportColumns.SEARCH_RESULT_AVAILABLE_COLUMNS.includes(col)
    );
  }

  public hasLastActivityAfterXFilter(): boolean {
    if (!propExists(this.filters)) { return false; }

    return (
      propExists(this.filters.last_activity_timestamp_min) &&
      !propExists(this.filters.last_activity_timestamp_max)
    );
  }

  public handleExpandSearch() {
    if (!this.hasLastActivityAfterXFilter()) { return; }

    this.filters.last_activity_timestamp_min = undefined;
    this.loadingCustomers = true;
    this.handleFilterChange();
  }

  private loadTags() {
    this.loadingTags = true;

    this.customerService
      .getCustomerTags(1, 1000)
      .then(response => {
        this.tags = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTags = false; });
  }

  private loadEventTypes() {
    this.loadingEventTypes = true;

    this.eventTypeService
      .getEventTypes(1, 3000)
      .then(response => {
        this.eventTypes = response.results
          .filter((value) => {
            return (value.status !== EventTypeStatus.draft);
          })
          .sort((a, b) => {
            if (a.status !== b.status) {
              if (a.status === EventTypeStatus.active) { return -1; }
              else { return 1; }
            }
            return (a.name > b.name) ? 1 : -1;
          });
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventTypes = false; });
  }

  private loadProficiencies() {
    this.loadingProficiencies = true;

    this.proficiencyService
      .getProficiencies(1, 1000)
      .then(response => {
        this.proficiencies = response.results
          .filter((value) => {
            return !value.is_removed;
          })
          .sort((a, b) => {
            return (a.name > b.name) ? 1 : -1;
          });
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingProficiencies = false; });
  }

  private loadAssociations() {
    this.loadingAssociations = true;

    this.associationService
      .getAssociations(1, 1000)
      .then(response => {
        this.associations = response.results
          .sort((a, b) => {
            return (a.name > b.name) ? 1 : -1;
          });
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingAssociations = false; });
  }

  private loadChallenges() {
    this.loadingChallenges = true;

    this.challengeService
      .getChallenges(1, 3000)
      .then(response => {
        this.challenges = response.results
          .sort((a, b) => {
            return (a.name > b.name) ? 1 : -1;
          });
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingChallenges = false; });
  }

  private loadCustomCustomerFields() {
    this.customCustomerFieldsLoading = true;
    this.orgConfigService
      .getCustomCustomerFields(1, 3000)
      .then(response => {
        this.customCustomerFields = response.results;
      })
      .finally(() => { this.customCustomerFieldsLoading = false; });
  }

  private hasSearchFilterApplied() {
    return !!this.filters.search;
  }

  /**
   * Navigate to the bulk add credit or voucher component, with the selected
   * customer IDs and emails in the history state.
   */
  private goToAddCreditOrVoucherForCustomers(customerIds: number[],
                                             customerNames: {[key: number]: string}) {
    this.router.navigate(
      ['/customers/bulk/add-credit-or-voucher'],
      {
        state: {
          customerIds,
          customerNames
        }
      }
    );
  }

  /**
   * Navigate to the bulk create booking component, with the selected
   * customer IDs and emails in the history state.
   */
  private goToCreateBookingForCustomers(customerIds: number[],
                                        customerNames: {[key: number]: string}) {
    this.router.navigate(
      ['/customers/bulk/create-booking'],
      {
        state: {
          customerIds,
          customerNames
        }
      }
    );
  }

  private openBulkAddOrRemoveTagModalForCustomers(isAdd: boolean,
                                                  customerIds: number[]) {
    if (this.loadingTags) { return; }

    this.modalResult = null;
    this.createdCustomer = null;

    const modalRef = this.modalService.open(BulkAddOrRemoveCustomerTagModalComponent);
    modalRef.componentInstance.customerIds = customerIds;
    modalRef.componentInstance.tags = this.tags;
    modalRef.componentInstance.isAdd = isAdd;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadReport();
      this.loadTags();
    };
  }

  private openBulkAddOrRemoveChallengeModalForCustomers(isAdd: boolean,
                                                        customerIds: number[]) {
    if (this.loadingTags) { return; }

    this.modalResult = null;
    this.createdCustomer = null;

    const modalRef = this.modalService.open(BulkAddOrRemoveCustomerChallengeModalComponent);
    modalRef.componentInstance.customerIds = customerIds;
    modalRef.componentInstance.isAdd = isAdd;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadReport();
    };
  }

  private openBulkAddToAssociationModalForCustomers(customerIds: number[]) {
    if (this.loadingAssociations) { return; }

    this.modalResult = null;
    this.createdCustomer = null;

    const modalRef = this.modalService.open(
      BulkAddCustomersToAssociationModalComponent
    );
    modalRef.componentInstance.customerIds = customerIds;
    modalRef.componentInstance.associations = this.associations;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
    };
  }

  private openBulkAddGuestPassesModalForCustomers(customerIds: number[]) {
    this.modalResult = null;
    this.createdCustomer = null;

    const modalRef = this.modalService.open(
      BulkCreateGuestPassesModalComponent
    );
    modalRef.componentInstance.customerIds = customerIds;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
    };
  }

  private openBulkAddAlertModalForCustomers(customerIds: number[]) {
    this.modalResult = null;
    this.createdCustomer = null;

    const modalRef = this.modalService.open(
      BulkAddCustomerAlertModalComponent
    );
    modalRef.componentInstance.customerIds = customerIds;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
    };
  }

  private openBulkSendAllthenticateInviteModalForCustomers(customerIds: number[]) {
    this.modalResult = null;
    this.createdCustomer = null;

    const modalRef = this.modalService.open(
      BulkSendAllthenticateInviteModalComponent
    );
    modalRef.componentInstance.customerIds = customerIds;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
    };
  }

  private obtainCustomersForBulkAction(includeNames: boolean,
                                       resultCallback: (result: BulkOperationCustomers) => void) {
    this.modalResult = null;
    this.createdCustomer = null;

    const modalRef = this.modalService.open(
      ObtainBulkOperationCustomersModalComponent
    );
    modalRef.componentInstance.getPage = (pageNum: number) => {
      if (this.displayCheckInRangeCount) {
        return this.customerService.getCustomersWithCheckInCountInRange(
          pageNum,
          OBTAIN_BULK_CUSTOMERS_PAGE_SIZE,
          this.filters,
          true
        );
      } else {
        return this.customerService.getCustomers(
          pageNum,
          OBTAIN_BULK_CUSTOMERS_PAGE_SIZE,
          this.filters,
          true
        );
      }
    };
    modalRef.componentInstance.totalNumPages = Math.ceil(this.customerList.count / OBTAIN_BULK_CUSTOMERS_PAGE_SIZE);
    modalRef.componentInstance.includeNames = includeNames;
    modalRef.componentInstance.resultCallback = resultCallback;
  }

}
